import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import useHRCopy from '../hr/i18n/useHRCopy';

// The contact form itself, without the surrounding section chrome, so pages
// can place it inside their own layout (İletişim puts it beside the channel
// list). Submit/validation behaviour is unchanged.
export default function ContactFormPanel({ delay = 0.09 }) {
  const t = useHRCopy().forms.contact;
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [messageInfo, setMessageInfo] = useState({ type: '', text: '' });

  useEffect(() => {
    if (messageInfo.text) {
      const timer = setTimeout(() => {
        setMessageInfo({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [messageInfo]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!form.name || !form.email || !form.message) {
      setMessageInfo({ type: 'error', text: t.fillAll });
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setMessageInfo({ type: 'error', text: t.invalidEmail });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL_USER}/contact`, form);
      if (response.status === 201 || response.status === 200) {
        setMessageInfo({ type: 'success', text: t.sent });
        setForm({ name: '', email: '', message: '' });
      }
    }
    catch (error) {
      const msg = error.response?.data?.errors?.[0]?.msg || t.failed;
      setMessageInfo({ type: 'error', text: msg });
    }
    finally {
      setLoading(false);
    }
  };

  const field =
    "w-full px-4 py-3 rounded-sm border border-line bg-white focus:ring-2 focus:ring-royal/40 focus:border-royal focus:outline-none text-sm transition";

  return (
    <motion.form
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ once: true }}
      onSubmit={handleSubmit}
      className="bg-white rounded-sm p-8 space-y-5 border border-line font-hanken"
    >
      <input
        type="text"
        name="name"
        placeholder={t.name}
        aria-label={t.name}
        value={form.name}
        onChange={handleChange}
        required
        className={field}
      />
      <input
        type="email"
        name="email"
        placeholder={t.email}
        aria-label={t.email}
        value={form.email}
        onChange={handleChange}
        required
        className={field}
      />
      <textarea
        name="message"
        placeholder={t.message}
        aria-label={t.message}
        rows="4"
        value={form.message}
        onChange={handleChange}
        required
        className={`${field} resize-none`}
      />
      <button
        type="submit"
        className="w-full bg-navy hover:bg-ink text-white font-semibold py-3 rounded-sm transition-colors duration-300"
        disabled={loading}
      >
        {loading ? t.sending : t.send}
      </button>
      {messageInfo.text && (
        <div
          className={`text-sm px-4 py-3 rounded-sm font-medium ${
            messageInfo.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-300'
              : 'bg-red-50 text-red-800 border border-red-300'
          }`}
        >
          {messageInfo.text}
        </div>
      )}
    </motion.form>
  );
}
